import { call, put, select } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { t } from "i18next";

import {
  IApiError,
  ToggleActionType,
  AlertStatusMessage, INonInventory,
} from '@/shared/types';

import { commonSlice } from "@/entities/common";
import { alertsSagaActions } from "@/entities/alerts";

import { asideSlice } from "@/widgets/side-block";
import { dialogSagaActions, dialogSlice } from "@/widgets/dialog";

import { selectParams } from "../model/selectors";
import { nonInventorySlice } from "../model/slice";
import {
  INonInventoryModified,
  IGetManyNonInventoryParams,
} from "../model/types/interfaces";
import {
  sendCreateNonInventoryRequest,
  sendGetNonInventoryRequest,
  sendUpdateNonInventoryRequest,
  sendDeleteNonInventoryRequest,
  sendGetManyNonInventoryRequest,
} from "./senders";
import {
  modifyOneNonInventoryData,
  modifyManyNonInventoryData,
  getUpdateDtoByFormData,
} from "./helpers";
import {
  GetNonInventoryPayload,
  GetNonInventoryResponse,
  UpdateNonInventoryPayload,
  DeleteNonInventoryPayload,
  CreateNonInventoryPayload,
  GetManyNonInventoryPayload,
  GetManyNonInventoryResponse, UpdateNonInventoryResponse,
} from './types/types';

export function* handleUpdateNonInventory(
  action: PayloadAction<UpdateNonInventoryPayload>
) {
  const { requestId, formData } = action.payload;
  const { updateAlert } = alertsSagaActions;
  const { toggleDialog } = dialogSagaActions;
  const { setActiveEntity } = commonSlice;

  const updateDto: Partial<INonInventory> = yield call(
    getUpdateDtoByFormData,
    formData
  );

  try {
    const response: UpdateNonInventoryResponse = yield call(sendUpdateNonInventoryRequest, requestId, updateDto);
    const request: INonInventory = response.data.data;

    yield put(setActiveEntity({ activeEntity: request }));
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("request_updated_successfully")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(toggleDialog({ status: ToggleActionType.CLOSE, isntEntityClear: true }));
    yield call(handleRetrieveManyNonInventory);
  }
}

export function* handleCreateNonInventory(
  action: PayloadAction<CreateNonInventoryPayload>
) {
  const { id, message } = action.payload;
  const { updateAlert } = alertsSagaActions;
  const { toggleDialog } = dialogSagaActions;

  try {
    yield call(sendCreateNonInventoryRequest, message, id);
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("request_created_successfully")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(toggleDialog({ status: ToggleActionType.CLOSE }));
  }
}


export function* handleDeleteNonInventory(
  action: PayloadAction<DeleteNonInventoryPayload>
) {
  const { requestId } = action.payload;
  const { updateAlert } = alertsSagaActions;
  const { toggleDialog } = dialogSagaActions;

  try {
    yield call(sendDeleteNonInventoryRequest, requestId);
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("request_deleted_successfully")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(toggleDialog({ status: ToggleActionType.CLOSE }));
    yield call(handleRetrieveManyNonInventory);
  }
}

export function* handleRetrieveNonInventory(action: PayloadAction<GetNonInventoryPayload>) {
  const { requestId } = action.payload;
  const { setActiveEntity } = commonSlice;
  const { setLoadingAside } = asideSlice;
  const { setLoadingDialog } = dialogSlice;

  try {
    const response: GetNonInventoryResponse = yield call(
      sendGetNonInventoryRequest,
      requestId
    );
    const request: INonInventory = response.data.data;
    const modifiedNonInventory: INonInventoryModified = yield call(
      modifyOneNonInventoryData,
      request
    );
    yield put(setActiveEntity({ activeEntity: modifiedNonInventory }));
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(setLoadingAside({ isLoading: false }));
    yield put(setLoadingDialog({ isLoading: false }));
  }
}

export function* handleRetrieveManyNonInventory(
  action?: PayloadAction<GetManyNonInventoryPayload>
) {
  const { setNonInventory, setParams } = nonInventorySlice;
  const { setLoading } = commonSlice;

  if (action?.payload?.params) {
    yield put(setParams({ params: action.payload.params }));
  }

  try {
    yield put(setLoading({ isLoading: true }));
    const bodyParams: IGetManyNonInventoryParams = yield select(selectParams);
    const response: GetManyNonInventoryResponse = yield call(
      sendGetManyNonInventoryRequest,
      bodyParams
    );
    const { items, count } = response.data.data;
    const modifiedManyNonInventory: Array<INonInventoryModified> = yield call(
      modifyManyNonInventoryData,
      items
    );

    yield put(setNonInventory({ data: { items: modifiedManyNonInventory, count } }));
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(setLoading({ isLoading: false }));
  }
}

function* handleError(err: any) {
  const { updateAlert } = alertsSagaActions;
  const error = err as IApiError;

  if (error?.response?.data?.error?.message?.length) {
    for (const text of error.response.data.error.message) {
      yield put(
        updateAlert({ status: AlertStatusMessage.ERROR, message: text })
      );
    }
  }
}
